import { useMemo, useRef, useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import type { ColumnDef } from '@tanstack/react-table'
import { Archive, FlaskConical, KanbanSquare, List, Plus, RefreshCw } from 'lucide-react'
import { toast } from 'sonner'
import {
  Button,
  Chip,
  DataTable,
  EmptyState,
  MetricCard,
  MetricStrip,
  PageHeaderBand,
  SearchInput,
  Select,
  Toolbar,
  ToolbarLeft,
  ToolbarRight,
  ViewToggle,
} from '@/components/ui'
import { useHasAnyPermission } from '@/shell/module-system'
import { CREATE_ROLES, EDIT_ROLES } from '../utils/permissions'
import {
  useSampleRequestList,
  useSampleRequestStats,
  useTransitionSampleRequest,
} from '../api/queries'
import type { SampleRequestSummary, Stage } from '../api/types'
import {
  BOARD_STAGES,
  compareByNeedBy,
  needByUrgencyForStage,
  STAGE_LABELS,
  STAGE_SHORT_LABELS,
  STAGE_VARIANT,
} from '../utils/stage-meta'
import { SampleRequestsKanban } from '../components/SampleRequestsKanban'
import { NewSampleRequestDrawer } from '../components/NewSampleRequestDrawer'

type ViewMode = 'kanban' | 'table'

const VIEW_OPTIONS = [
  { value: 'kanban' as const, label: 'Kanban', icon: KanbanSquare },
  { value: 'table' as const, label: 'Table', icon: List },
]

type StageFilter = 'all' | Stage

const STAGE_FILTER_OPTIONS: { value: StageFilter; label: string }[] = [
  { value: 'all', label: 'All stages' },
  ...BOARD_STAGES.map((s) => ({ value: s, label: STAGE_LABELS[s] })),
]

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
}

export function SampleRequestsPage() {
  const navigate = useNavigate()
  const [stageFilter, setStageFilter] = useState<StageFilter>('all')
  const [searchTerm, setSearchTerm] = useState('')
  const [view, setView] = useState<ViewMode>('kanban')
  const [showArchived, setShowArchived] = useState(false)
  const [overdueOnly, setOverdueOnly] = useState(false)
  const [newOpen, setNewOpen] = useState(false)
  const searchInputRef = useRef<HTMLInputElement>(null)

  // The external uviva role may create a request but not move stages; editors
  // get everything. See utils/permissions.ts (mirrors the backend gates).
  const canEdit = useHasAnyPermission(EDIT_ROLES)
  const canCreate = useHasAnyPermission(CREATE_ROLES)

  const openRequest = (id: string) =>
    navigate({ to: '/sample-requests/$requestId', params: { requestId: id } })

  const stats = useSampleRequestStats()
  const list = useSampleRequestList({
    ...(stageFilter === 'all' ? {} : { status: stageFilter }),
    archived: showArchived,
    overdue_only: overdueOnly,
  })
  const transition = useTransitionSampleRequest()

  const items = list.data?.items ?? []
  const filtered = searchTerm.trim()
    ? items.filter((row) => {
        const needle = searchTerm.trim().toLowerCase()
        return (
          row.customer_name?.toLowerCase().includes(needle) ||
          row.prospect_name?.toLowerCase().includes(needle) ||
          row.request_number.toLowerCase().includes(needle) ||
          row.tracking_number?.toLowerCase().includes(needle) ||
          row.crm_lead_name?.toLowerCase().includes(needle)
        )
      })
    : items

  // Need-by date is the triage signal — order soonest-first, blanks last.
  // The table's need_by column stays user-sortable on top of this default.
  const ordered = useMemo(() => [...filtered].sort(compareByNeedBy), [filtered])

  const columns = useColumns()

  return (
    <div className="flex h-full min-h-0 w-full flex-col bg-canvas">
      <PageHeaderBand
        title="Uviva Samples"
        description="One place to request Uviva samples and track them from request through delivery and follow-up."
      >
        <div className="ml-auto flex items-center gap-3">
          <MetricStrip variant="inline">
            <MetricCard variant="inline" label="Open" value={String(stats.data?.open ?? 0)} />
            <MetricCard
              variant="inline"
              label="Awaiting ship"
              value={String(stats.data?.awaiting_ship ?? 0)}
            />
            <MetricCard variant="inline" label="Overdue" value={String(stats.data?.overdue ?? 0)} />
            <MetricCard variant="inline" label="Shipped" value={String(stats.data?.shipped ?? 0)} />
          </MetricStrip>
          {canCreate ? (
            <Button variant="primary" size="sm" onClick={() => setNewOpen(true)}>
              <Plus size={14} className="mr-1.5" /> New request
            </Button>
          ) : null}
        </div>
      </PageHeaderBand>

      <Toolbar>
        <SearchInput
          ref={searchInputRef}
          containerClassName="flex-1 max-w-none"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search customer, SR #, tracking, opportunity…"
        />
      </Toolbar>

      <Toolbar>
        <ToolbarLeft>
          <Select
            value={stageFilter}
            onChange={(e) => setStageFilter(e.target.value as StageFilter)}
            aria-label="Filter by stage"
          >
            {STAGE_FILTER_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </Select>
          <Button
            variant={overdueOnly ? 'primary' : 'secondary'}
            size="sm"
            onClick={() => setOverdueOnly((v) => !v)}
            aria-pressed={overdueOnly}
          >
            Overdue only
          </Button>
          <Button
            variant={showArchived ? 'primary' : 'secondary'}
            size="sm"
            onClick={() => setShowArchived((v) => !v)}
            aria-pressed={showArchived}
          >
            <Archive size={14} className="mr-1.5" />
            {showArchived ? 'Showing closed' : 'Closed'}
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => list.refetch()}
            disabled={list.isFetching}
          >
            <RefreshCw size={14} className="mr-1.5" />
            Refresh
          </Button>
        </ToolbarLeft>
        <ToolbarRight>
          <ViewToggle<ViewMode>
            options={VIEW_OPTIONS}
            value={view}
            onChange={setView}
            aria-label="View mode"
          />
        </ToolbarRight>
      </Toolbar>

      <div className="flex-1 min-h-0 overflow-y-auto px-6 py-4">
        {list.isLoading ? (
          <div className="flex flex-col items-center justify-center py-12">
            <p className="m-0 text-sm text-tertiary">Loading…</p>
          </div>
        ) : filtered.length === 0 ? (
          <EmptyState
            icon={FlaskConical}
            title="No sample requests here yet"
            description={
              stageFilter === 'all' && !showArchived && !overdueOnly
                ? 'Click “New request” to log a Uviva sample request. The sample team is notified automatically and you can track it through delivery here.'
                : 'No requests match the current filters.'
            }
          />
        ) : view === 'kanban' && !showArchived ? (
          <SampleRequestsKanban
            items={ordered}
            readOnly={!canEdit}
            onSelect={(req) => openRequest(req.id)}
            onTransition={(req, target) => {
              transition.mutate(
                { requestId: req.id, targetStage: target },
                {
                  onError: (err) => {
                    const msg = err instanceof Error ? err.message : 'transition failed'
                    toast.error(msg, { duration: 5000 })
                  },
                },
              )
            }}
          />
        ) : (
          <DataTable
            data={ordered}
            columns={columns}
            loading={list.isLoading}
            getRowId={(r) => r.id}
            onRowClick={(r) => openRequest(r.id)}
            moduleId="sample-requests"
            maxHeight="100%"
            columnWidths={{
              request_number: 110,
              customer: 200,
              stage: 130,
              contact: 160,
              owner: 180,
              need_by: 150,
              tracking: 160,
              created_at: 110,
            }}
            minTableWidth={1100}
            emptyState={
              <EmptyState
                variant="no-match"
                title="No matches"
                description="Try clearing the stage filter or your search."
              />
            }
          />
        )}
      </div>

      <NewSampleRequestDrawer open={newOpen} onOpenChange={setNewOpen} onCreated={openRequest} />
    </div>
  )
}

function useColumns(): ColumnDef<SampleRequestSummary, unknown>[] {
  return useMemo<ColumnDef<SampleRequestSummary, unknown>[]>(
    () => [
      {
        id: 'request_number',
        header: 'Request',
        accessorKey: 'request_number',
        cell: (ctx) => (
          <span className="text-role-data-primary">{ctx.row.original.request_number}</span>
        ),
      },
      {
        id: 'customer',
        header: 'Customer',
        accessorFn: (r) => r.customer_name ?? r.prospect_name ?? '',
        cell: (ctx) => {
          const r = ctx.row.original
          return r.customer_name ?? r.prospect_name ?? <em className="text-tertiary">—</em>
        },
      },
      {
        id: 'stage',
        header: 'Stage',
        accessorFn: (r) => STAGE_SHORT_LABELS[r.status],
        cell: (ctx) => (
          <div className="flex items-center gap-2">
            <Chip size="xs" variant={STAGE_VARIANT[ctx.row.original.status]} dot>
              {STAGE_SHORT_LABELS[ctx.row.original.status]}
            </Chip>
            {ctx.row.original.overdue ? (
              <Chip size="xs" variant="danger">
                Overdue
              </Chip>
            ) : null}
          </div>
        ),
      },
      {
        id: 'contact',
        header: 'Contact',
        accessorFn: (r) => r.contact_name ?? '',
        cell: (ctx) => (
          <span className="text-secondary">
            {ctx.row.original.contact_name ?? <em className="text-tertiary">—</em>}
          </span>
        ),
      },
      {
        id: 'owner',
        header: 'Owner',
        accessorFn: (r) => r.assigned_to ?? '',
        cell: (ctx) => (
          <span className="text-secondary">
            {ctx.row.original.assigned_to ?? <em className="text-tertiary">unassigned</em>}
          </span>
        ),
      },
      {
        id: 'need_by',
        header: 'Need by',
        accessorFn: (r) => r.need_by_date ?? '',
        cell: (ctx) => {
          const urgency = needByUrgencyForStage(
            ctx.row.original.need_by_date,
            ctx.row.original.status,
          )
          return (
            <div className="flex items-center gap-2">
              <span className="text-tertiary">{formatDate(ctx.row.original.need_by_date)}</span>
              {urgency ? (
                <Chip size="xs" variant={urgency.variant}>
                  {urgency.label}
                </Chip>
              ) : null}
            </div>
          )
        },
      },
      {
        id: 'tracking',
        header: 'Tracking',
        accessorFn: (r) => r.tracking_number ?? '',
        cell: (ctx) => (
          <span className="text-secondary">{ctx.row.original.tracking_number ?? '—'}</span>
        ),
      },
      {
        id: 'created_at',
        header: 'Requested',
        accessorKey: 'created_at',
        cell: (ctx) => (
          <span className="text-tertiary">{formatDate(ctx.row.original.created_at)}</span>
        ),
      },
    ],
    [],
  )
}
